import { Dispatch } from 'redux'
import {
    Dispatch as ReactDispatch,
    RefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { Observable, Subject } from 'rxjs'
import useObservable from 'react-use/lib/useObservable'
import { RootActions, RootState } from '../redux/root'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import { FileData, isTempFileData, TempFile } from '../responses/files'
import { RequestStatus } from '../responses/response'
import { concat, getIdGenerator, shallowSet } from './utils'
import { uploadFiles } from '../redux/global/files'
import { difference, zip } from 'fp-ts/es6/Array'
import { entityArrayReplace, entityRemove } from './entity-utils'
import { echoServer } from '../echo'
import { useKey, useScroll, useUpdateEffect } from 'react-use'
import { tempStatus } from './lenses'
import useMeasureDirty from 'react-use/lib/useMeasureDirty'
import { eqString } from 'fp-ts/es6/Eq'
import { intersection } from 'fp-ts/Array'
import { isHtmlElement } from './type-guards'

export type SubjectOperation<Input, Output> = (source$: Subject<Input>) => Observable<Output>

/**
 @deprecated use observable-hooks instead
*/
export const useSubject = <Input, Output>(initial: Output, operation: SubjectOperation<Input, Output>): [Subject<Input>, Output] => {
    const { current: input$ } = useRef(new Subject<Input>())
    const { current: output$ } = useRef(operation(input$))
    const output = useObservable(output$, initial)
    return [input$, output]
}

type CallBack<Frame> = (frame: Frame) => void

export const useSocket = <Frame, NotifyFrame = Frame>(channel: string, event: string, dependencies: unknown[], listen: CallBack<Frame>, notify?: CallBack<NotifyFrame>): void => {
    useEffect(() => {
        if (dependencies.every(dep => !!dep)) {
            const serverChannel = echoServer.private(channel).listen(event, listen)
            if (notify) {
                serverChannel.notification(notify)
            }
        }
        return () => echoServer.leave(channel)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel, event, ...dependencies])
}

export const useRootDispatch = (): Dispatch<RootActions> => useDispatch<Dispatch<RootActions>>()

export const useRootSelector: TypedUseSelectorHook<RootState> = useSelector

interface StayScrolledResult {
    scroll: (position: number) => void,
    stayScrolled: () => void,
    scrollTop: () => void,
    scrollBottom: () => void,
    scrollEnd: () => void,
    isScrolled: () => boolean,
}

// copy of react-stay-scrolled with null checks
const maxScrollTop = <T extends HTMLElement>(dom: T | null) => {
    return (dom?.scrollHeight || 0) - (dom?.clientHeight || 0)
}

export const useStayScrolled = <T extends HTMLElement>(ref: RefObject<T>, options?: {
    initialScroll?: number,
    runScroll?: (offset: number) => void,
    reversed?: boolean,
}): StayScrolledResult => {
    const runScroll = useCallback((offset: number) => {
        if (options?.runScroll) {
            options.runScroll(offset)
        } else {
            ref.current?.scrollTo(0, offset)
        }
    }, [options, ref])
    const wasScrolled = useRef(false)

    const isScrolled = useCallback(() => {
        return options?.reversed ?
            Math.ceil(ref.current?.scrollTop || 0) >= maxScrollTop(ref.current) :
            ref.current?.scrollTop == 0
    }, [options?.reversed, ref])

    useEffect(() => {
        const current = ref.current
        const onScroll = () => {
            wasScrolled.current = isScrolled()
        }

        current?.addEventListener('scroll', onScroll)
        return () => current?.removeEventListener('scroll', onScroll)
    }, [isScrolled, ref])

    const scroll = useCallback((position: number) => {
        const offset = options?.reversed ?
            Math.min(maxScrollTop(ref.current), position) :
            Math.max(ref.current?.scrollTop || 0, position)
        runScroll(offset)
    }, [options?.reversed, ref, runScroll])

    const scrollTop = useCallback(() => scroll(0), [scroll])
    const scrollBottom = useCallback(() => scroll(maxScrollTop(ref.current)), [ref, scroll])
    const scrollEnd = useCallback(() => {
        if (options?.reversed) {
            scrollBottom()
        } else {
            scrollTop()
        }
    }, [options?.reversed, scrollBottom, scrollTop])

    const stayScrolled = useCallback(() => {
        if (wasScrolled.current) {
            scrollEnd()
        }
        return wasScrolled.current
    }, [scrollEnd])

    useUpdateEffect(() => {
        scrollEnd()
        wasScrolled.current = isScrolled()
    }, [options?.reversed])

    return {
        scroll,
        stayScrolled,
        scrollTop,
        scrollBottom,
        scrollEnd,
        isScrolled,
    }
}

export interface UploadData {
    set: (files: FileData[]) => void,
    files: TempFile[],
    clear: () => void,
    upload: (files: File[]) => void,
    remove: (file: TempFile) => void,
    isUploaded: boolean,
}

const idGenerator = getIdGenerator()

const tempFromFile = (data: FileData): TempFile => ({
    id: idGenerator.next().value,
    data,
    status: RequestStatus.SUCCESS,
})

const emptyArray: FileData[] = []

export const useUpload = (init: FileData[] = emptyArray, onChange?: (files: FileData[]) => void): UploadData => {
    const [temps, setTemps] = useState<TempFile[]>(init.map(tempFromFile))

    useEffect(() => setTemps(init.map(tempFromFile)), [init])

    const set = useCallback((newFiles: FileData[]) => {
        setTemps(shallowSet(newFiles.map(tempFromFile)))
    }, [])

    const upload = useCallback((files: File[]) => {
        const tempFiles: TempFile[] = files.map(data => ({
            id: idGenerator.next().value,
            data,
            status: RequestStatus.FETCHING,
        }))
        setTemps(concat<TempFile>(tempFiles))
        uploadFiles(files).subscribe(
            ({ rows }) => {
                const newTemps: TempFile[] = zip(tempFiles, rows).map(([tempFile, data]) => ({
                    id: tempFile.id,
                    status: RequestStatus.SUCCESS,
                    data,
                }))
                setTemps(entityArrayReplace(newTemps))
            },
            () => setTemps(entityArrayReplace(tempFiles.map(tempStatus.set(RequestStatus.FAILURE))))
        )
    }, [])

    const remove = useCallback((file: TempFile) => {
        setTemps(entityRemove(file))
    }, [])

    const clear = useCallback(() => setTemps([]), [])

    const isUploaded = useMemo(() => temps.every(file => file.status !== RequestStatus.FETCHING), [temps])

    useEffect(() => {
        onChange?.(temps.filter(isTempFileData).map(file => file.data))
    }, [temps, onChange])

    return {
        set,
        files: temps,
        clear,
        upload,
        remove,
        isUploaded,
    }
}

export const useOverflow = <T extends HTMLElement>(ref: RefObject<T>): boolean => {
    const [overflow, setOverflow] = useState(false)
    const {width, height} = useMeasureDirty(ref)

    useLayoutEffect(() => {
        if (ref.current) {
            const { scrollWidth, scrollHeight, clientWidth, clientHeight } = ref.current
            setOverflow((scrollHeight > clientHeight) || (scrollWidth > clientWidth))
        }
    }, [ref.current?.scrollHeight, ref.current?.scrollWidth, ref.current?.clientHeight, ref.current?.clientWidth, ref, width, height])

    return overflow
}

interface ScrollBounds {
    top: boolean,
    left: boolean,
    right: boolean,
    bottom: boolean,
}

export const useScrollBounds = <T extends HTMLElement>(ref: RefObject<T>): ScrollBounds => {
    const { x, y } = useScroll(ref)
    const [bounds, setBounds] = useState<ScrollBounds>({
        top: false,
        left: false,
        right: false,
        bottom: false,
    })

    useLayoutEffect(() => {
        if (ref.current) {
            const { scrollWidth, scrollHeight, clientWidth, clientHeight } = ref.current
            setBounds(shallowSet({
                top: y === 0,
                left: x === 0,
                right: x === scrollWidth - clientWidth,
                bottom: y === scrollHeight - clientHeight,
            }))
        }
    }, [x, y, ref, ref.current?.scrollWidth, ref.current?.scrollHeight, ref.current?.clientWidth, ref.current?.clientHeight])

    return bounds
}

export const useDerivedState = <T>(value: T): [T, ReactDispatch<SetStateAction<T>>] => {
    const [state, setState] = useState(value)
    useEffect(() => setState(value), [value])
    return [state, setState]
}

interface BooleanResult {
    state: boolean,
    setTrue: () => void,
    setFalse: () => void,
    toggle: () => void,
}

export const useBoolean = (value?: boolean): BooleanResult => {
    const [state, setState] = useState(value ?? false)

    const setTrue = useCallback(() => setState(true), [])
    const setFalse = useCallback(() => setState(false), [])
    const toggle = useCallback(() => setState(state => !state), [])

    return {
        state,
        setTrue,
        setFalse,
        toggle,
    }
}

const isNotInput = (element: Element | null): boolean => {
    return !(isHtmlElement(element) && (!!element.contentEditable || element.tagName === 'input'))
}

export const useNavigation = <T extends HTMLElement>(ref: RefObject<T>): void => {
    useKey('PageUp', () => {
        if (ref.current) {
            ref.current.scrollTop -= ref.current.clientHeight
        }
    })
    useKey('PageDown', () => {
        if (ref.current) {
            ref.current.scrollTop += ref.current.clientHeight
        }
    })
    useKey('Home', () => {
        if (ref.current && isNotInput(document.activeElement)) {
            ref.current.scrollTop = 0
        }
    })
    useKey('End', () => {
        if (ref.current && isNotInput(document.activeElement)) {
            ref.current.scrollTop = ref.current.scrollHeight
        }
    })
}

export const useAllPermissions = (permissions: string[]): boolean => {
    const statePermissions = useRootSelector(state => state.global.user.data.permissions ?? [])
    return difference(eqString)(permissions, statePermissions).length === 0
}

export const useAnyPermissions = (permissions: string[]): boolean => {
    const statePermissions = useRootSelector(state => state.global.user.data.permissions ?? [])
    return intersection(eqString)(permissions, statePermissions).length > 0
}